import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  CACHE_TTL_MS,
  PluginListContext,
  loadPluginList,
  pluginCache,
} from "./PluginListContextUtils";
import type { PluginListData, PluginListEntry } from "../model/Model";

export function PluginListProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<PluginListData | null>(pluginCache.data);
  const [loading, setLoading] = useState<boolean>(!pluginCache.data);
  const [error, setError] = useState(false);

  const pluginMap = useMemo(() => {
    const map = new Map<string, PluginListEntry>();
    for (const p of data?.plugins ?? []) map.set(p.plugin_url, p);
    return map;
  }, [data]);

  const refresh = useCallback(async () => {
    const result = await loadPluginList();
    if (result) {
      setData(result);
      setError(false);
    } else {
      setError(true);
    }
  }, []);

  useEffect(() => {
    const sub = (next: PluginListData | null) => {
      if (next) {
        setData(next);
        setError(false);
      }
    };
    pluginCache.subscribers.add(sub);
    return () => {
      pluginCache.subscribers.delete(sub);
    };
  }, []);

  useEffect(() => {
    const fresh =
      pluginCache.data && Date.now() - pluginCache.fetchedAt < CACHE_TTL_MS;
    if (fresh) {
      if (data !== pluginCache.data) setData(pluginCache.data);
      setLoading(false);
      return;
    }
    loadPluginList()
      .then((result) => {
        if (!result) setError(true);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PluginListContext.Provider
      value={{
        data,
        plugins: data?.plugins ?? [],
        pluginMap,
        loading,
        error,
        refresh,
      }}
    >
      {children}
    </PluginListContext.Provider>
  );
}
